import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, Mic, Share2, Check, Copy, Clock, CalendarClock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getTheme } from '../utils/colors';
import Lightbox from '../components/Lightbox';
import LeadCaptureForm from '../components/LeadCaptureForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to convert URLs in text to clickable links
const linkifyText = (text) => {
  if (!text) return null;
  
  // URL regex pattern
  const urlPattern = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  
  const parts = text.split(urlPattern);
  
  return parts.map((part, index) => {
    if (urlPattern.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Helper to update OG meta tags dynamically
const updateOGMetaTags = (metadata) => {
  // Helper to set or create meta tag
  const setMetaTag = (property, content) => {
    if (!content) return;
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  // Set OG tags
  setMetaTag('og:title', metadata.title);
  setMetaTag('og:description', metadata.description);
  setMetaTag('og:type', metadata.type || 'website');
  setMetaTag('og:url', window.location.href);
  
  if (metadata.image) {
    setMetaTag('og:image', `${BACKEND_URL}${metadata.image}`);
  }
  
  // Also set Twitter cards
  setMetaTag('twitter:card', metadata.image ? 'summary_large_image' : 'summary');
  setMetaTag('twitter:title', metadata.title);
  setMetaTag('twitter:description', metadata.description);
  if (metadata.image) {
    setMetaTag('twitter:image', `${BACKEND_URL}${metadata.image}`);
  }
  
  // Update page title
  if (metadata.title) {
    document.title = metadata.title;
  }
};

const ViewPage = () => {
  const { nfcCode } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [copied, setCopied] = useState(false);
  const [tagNotFound, setTagNotFound] = useState(false);
  const [leadSettings, setLeadSettings] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  
  // Get source from URL params (qr, link, or undefined for NFC)
  const source = searchParams.get('source');

  const shareUrl = `${window.location.origin}/${nfcCode}?source=link`;

  const handleShare = async () => {
    // Use Web Share API if available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: content?.description || 'Check this out!',
          text: content?.message?.substring(0, 100) || 'Someone shared this with you',
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to copy
          copyToClipboard();
        }
      }
    } else {
      // Fallback for desktop
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  useEffect(() => {
    loadContent();
  }, [nfcCode]);

  // Check if code looks like a UUID (random string) vs custom tag (readable)
  const isUuidLike = (code) => {
    // UUID pattern: contains multiple dashes and mostly random characters
    const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    const hasPrefix = code.includes('-') && code.split('-').length > 2;
    // If it matches UUID pattern or has a prefix followed by UUID
    return uuidPattern.test(code) || (hasPrefix && code.split('-').slice(-5).join('-').match(uuidPattern));
  };

  const loadContent = async () => {
    try {
      // Pass source parameter to track how user arrived (nfc, qr, or link)
      const params = source ? `?source=${source}` : '';
      const response = await axios.get(`${API}/tags/${nfcCode}${params}`);
      setContent(response.data);
      const selectedTheme = getTheme(response.data.theme || 'default');
      setTheme(selectedTheme);
      
      // Load lead capture settings
      try {
        const leadRes = await axios.get(`${API}/admin/tags/${nfcCode}/lead-settings`);
        setLeadSettings(leadRes.data);
        // Show lead form if enabled and timing is "before"
        if (leadRes.data?.lead_capture_enabled && leadRes.data?.capture_timing === 'before') {
          setShowLeadForm(true);
        }
      } catch (e) {
        // Lead settings not configured, that's okay
      }
      
      // Update OG meta tags for social sharing
      updateOGMetaTags({
        title: response.data.description || 'Check this out!',
        description: response.data.message?.substring(0, 200) || 'Someone shared this with you',
        image: response.data.media_files?.find(m => m.content_type?.startsWith('image/'))?.url,
        type: 'website'
      });
      
      // Hide UUID from URL bar - but keep custom tag codes visible
      const hasContent = response.data && (response.data.message || response.data.description || response.data.media_files?.length > 0);
      if (hasContent && isUuidLike(nfcCode)) {
        window.history.replaceState({}, '', '/');
      }
    } catch (error) {
      console.error('Error loading content:', error);
      if (error.response?.status === 404) {
        setTagNotFound(true);
      } else {
        toast.error('Failed to load content');
      }
    } finally {
      setLoading(false);
    }
  };

  const isEmpty = !content || (!content.message && !content.description && content.media_files?.length === 0);
  const isExpired = content?.is_expired;
  const isScheduled = content?.is_scheduled;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FDFCF8' }}>
        <div className="text-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  if (tagNotFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FDFCF8' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <ImageIcon className="w-10 h-10 text-red-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight mb-4">
            {t('view.invalidTag')}
          </h1>
          <p className="text-muted-foreground">
            {t('view.invalidTagDesc')}
          </p>
        </motion.div>
      </div>
    );
  }

  // Show expired message if content has expired
  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FDFCF8' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-6">
            <Clock className="w-10 h-10 text-orange-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight mb-4">
            {t('view.expired')}
          </h1>
          <p className="text-muted-foreground">
            {t('view.expiredDesc')}
          </p>
          {content?.expires_at && (
            <p className="text-sm text-muted-foreground mt-4">
              {t('view.expiredOn')}: {new Date(content.expires_at).toLocaleDateString()}
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  // Show coming soon message if content is scheduled for future
  if (isScheduled) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FDFCF8' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-6">
            <CalendarClock className="w-10 h-10 text-blue-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight mb-4">
            {t('view.comingSoon')}
          </h1>
          <p className="text-muted-foreground">
            {t('view.comingSoonDesc')}
          </p>
          {content?.goes_live_at_formatted && (
            <p className="text-sm text-blue-600 mt-4 font-medium">
              {t('view.available')}: {content.goes_live_at_formatted}
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#FDFCF8' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="max-w-md w-full text-center"
        >
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto bg-secondary rounded-full flex items-center justify-center mb-6">
              <ImageIcon className="w-12 h-12 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-heading tracking-tight mb-4">
              {t('view.emptyTag')}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {t('view.emptyTagDesc')}
            </p>
          </div>
          <button
            data-testid="add-content-button"
            onClick={() => navigate(`/${nfcCode}/tag`)}
            className="bg-primary text-primary-foreground rounded-full px-8 py-6 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md w-full"
          >
            {t('edit')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: theme?.colors.background || '#FDFCF8' }}>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading tracking-tight mb-6"
              style={{ color: theme?.colors.foreground || '#18181B' }}
            >
              {content.description || 'My Memories'}
            </h1>
            
            {/* Share Button */}
            <motion.button
              data-testid="share-button"
              onClick={handleShare}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all shadow-md hover:shadow-lg"
              style={{
                background: theme?.colors.cardBg || '#FFFFFF',
                color: theme?.colors.foreground || '#18181B',
                border: `1px solid ${theme?.colors.primary || '#E4E4E7'}`
              }}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" strokeWidth={2} />
                  <span>{t('view.linkCopied')}</span>
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" strokeWidth={1.5} />
                  <span>{t('view.shareTitle')}</span>
                </>
              )}
            </motion.button>
          </div>

          {/* Message */}
          {content.message && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="rounded-3xl border shadow-lg p-8 mb-8"
              style={{
                background: theme?.colors.cardBg || '#FFFFFF',
                borderColor: theme?.colors.primary || '#E4E4E7',
                color: theme?.colors.foreground || '#18181B'
              }}
            >
              <p className="text-lg leading-relaxed whitespace-pre-wrap">{linkifyText(content.message)}</p>
            </motion.div>
          )}

          {/* Media Grid */}
          {content.media_files && content.media_files.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-6 mb-12"
            >
              <p 
                className="text-center text-sm opacity-60"
                style={{ color: theme?.colors.foreground || '#18181B' }}
              >
                Tap photo to view fullscreen
              </p>
              {content.media_files.map((media, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="rounded-3xl border shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  style={{
                    borderColor: theme?.colors.primary || '#E4E4E7',
                    background: theme?.colors.cardBg || '#FFFFFF'
                  }}
                  onClick={() => setLightboxIndex(index)}
                  data-testid={`media-card-${index}`}
                >
                  {media.content_type.startsWith('image/') ? (
                    <div 
                      className="w-full flex items-center justify-center"
                      style={{ background: theme?.colors.secondary || '#F4F4F5' }}
                    >
                      <img
                        data-testid={`image-display-${index}`}
                        src={`${BACKEND_URL}${media.url}`}
                        alt="Memory"
                        style={{ 
                          maxWidth: '100%',
                          maxHeight: '80vh',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          display: 'block'
                        }}
                      />
                    </div>
                  ) : media.content_type.startsWith('audio/') ? (
                    <div 
                      className="w-full p-8 flex flex-col items-center gap-4"
                      style={{ background: theme?.colors.secondary || '#F4F4F5' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Mic 
                        className="w-16 h-16" 
                        strokeWidth={1.5}
                        style={{ color: theme?.colors.foreground || '#18181B' }}
                      />
                      <audio
                        data-testid={`audio-player-${index}`}
                        src={`${BACKEND_URL}${media.url}`}
                        controls
                        className="w-full max-w-md"
                      />
                    </div>
                  ) : (
                    <video
                      data-testid={`video-player-${index}`}
                      src={`${BACKEND_URL}${media.url}`}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full h-auto rounded-lg"
                      style={{ maxHeight: '80vh', objectFit: 'contain' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Lightbox */}
          {lightboxIndex !== null && content.media_files && (
            <Lightbox
              media={content.media_files}
              currentIndex={lightboxIndex}
              onClose={() => setLightboxIndex(null)}
              onNavigate={setLightboxIndex}
            />
          )}

          {/* Lead Capture Form - Floating after content view */}
          <AnimatePresence>
            {leadSettings?.lead_capture_enabled && 
             leadSettings?.capture_timing === 'floating' && 
             !leadCaptured && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ delay: 3 }}
                className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto"
              >
                <LeadCaptureForm
                  nfcCode={nfcCode}
                  settings={leadSettings}
                  onClose={() => setLeadCaptured(true)}
                  onSuccess={() => setLeadCaptured(true)}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Powered by Footer */}
          <div className="mt-12 pt-6 border-t text-center" style={{ borderColor: theme?.colors.primary + '30' || '#E4E4E7' }}>
            <p className="text-sm" style={{ color: theme?.colors.foreground + '99' || '#71717A' }}>
              Powered by{' '}
              <a 
                href="https://scanit2go.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline font-medium"
                style={{ color: theme?.colors.primary || '#3B82F6' }}
              >
                scanit2go.com
              </a>
            </p>
          </div>
        </motion.div>

        {/* Lead Capture Overlay - Before content (gate) */}
        <AnimatePresence>
          {showLeadForm && leadSettings?.capture_timing === 'before' && !leadCaptured && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            >
              <LeadCaptureForm
                nfcCode={nfcCode}
                settings={leadSettings}
                onSuccess={() => {
                  setLeadCaptured(true);
                  setShowLeadForm(false);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ViewPage;